import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, TextField, IconButton, Grid, Typography, CircularProgress,
    Snackbar, Alert, Stack, MenuItem, Select, FormControl, InputLabel, Button,
    Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
    Edit as EditIcon,
    Check as ApproveIcon,
    Close as RejectIcon,
    RestartAlt as ResetIcon,
    SearchOff as NoDataIcon,
    AccountBalance as BankIcon,
    VerifiedUser as VerifiedIcon,
    People as PeopleIcon,
    AttachMoney as MoneyIcon,
    Close as CloseIcon,
    AssignmentInd as StatutoryIcon
} from "@mui/icons-material";
import { getData, putData } from "../../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../../services/endpoints";
import StatsCard from "../../../../../components/common/StatsCard";

const mockBanks = ['HDFC Bank', 'ICICI Bank', 'State Bank of India', 'Axis Bank', 'Kotak Mahindra Bank', 'Yes Bank', 'Other'];

function BankingAndStatutoryStepper({ batchData = [], payPeriod }) {
    const [bankingList, setBankingList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [bankStatusFilter, setBankStatusFilter] = useState("All");
    const [statutoryStatusFilter, setStatutoryStatusFilter] = useState("All");

    // Edit Modal State
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({
        bankName: "",
        otherBankName: "",
        branchName: "",
        accountNumber: "",
        ifscCode: "",
        panNumber: "",
        aadhaarNumber: "",
        uanNumber: "",
        pfNumber: "",
        esiNumber: "",
        grossSalary: 0,
        status: "Active"
    });
    const [saveLoading, setSaveLoading] = useState(false);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Feedback Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Fetch Banking and Statutory Details matching the API contract:
    // API returns employees array: [{ id, empId, employeeName, grossSalary, bankName, accountNumber, ifscCode, pfNumber, esiNumber, status }]
    const fetchBankingDetails = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getEmployeeBankingAndStatutoryDetails);
            
            // Handle array directly or nested employees array in response
            if (res?.statusCode === 200 || res?.status === 'SUCCESS' || Array.isArray(res)) {
                setBankingList(res.result?.employees || res.employees || res.data || (Array.isArray(res) ? res : []));
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Error fetching banking and statutory details",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    }, [batchData]);

    useEffect(() => {
        fetchBankingDetails();
    }, [fetchBankingDetails]);

    // --- Summary Metrics ---
    const summaryMetrics = useMemo(() => {
        const totalEmployees = bankingList.length;
        let bankVerifiedCount = 0;
        let pfRegisteredCount = 0;
        let esiRegisteredCount = 0;

        bankingList.forEach((item) => {
            if (item.isBankComplete) bankVerifiedCount += 1;
            if (item.pfNumber && item.pfNumber !== "-") pfRegisteredCount += 1;
            if (item.esiNumber && item.esiNumber !== "-") esiRegisteredCount += 1;
        });

        return { totalEmployees, bankVerifiedCount, pfRegisteredCount, esiRegisteredCount };
    }, [bankingList]);

    // --- Filter & Search ---
    const filteredList = useMemo(() => {
        return bankingList.filter((emp) => {
            const matchesSearch =
                !searchQuery ||
                emp.empId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.bankName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.pfNumber?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesBankStatus =
                bankStatusFilter === "All" ||
                (bankStatusFilter === "Complete" && emp.isBankComplete) ||
                (bankStatusFilter === "Incomplete" && !emp.isBankComplete);

            const matchesStatutoryStatus =
                statutoryStatusFilter === "All" ||
                (statutoryStatusFilter === "Complete" && emp.isStatutoryComplete) ||
                (statutoryStatusFilter === "Incomplete" && !emp.isStatutoryComplete);

            return matchesSearch && matchesBankStatus && matchesStatutoryStatus;
        });
    }, [bankingList, searchQuery, bankStatusFilter, statutoryStatusFilter]);

    // --- Edit Handlers ---
    const handleOpenEdit = (row) => {
        setEditingRow(row);
        const bankVal = row.bankName || row.bankingDetails?.bank_name || "";
        const isMockBank = mockBanks.includes(bankVal);
        setFormData({
            bankName: isMockBank ? bankVal : (bankVal ? "Other" : ""),
            otherBankName: !isMockBank && bankVal ? bankVal : "",
            branchName: row.branchName || row.bankingDetails?.branch_name || "",
            accountNumber: row.accountNumber || row.bankingDetails?.account_number || "",
            ifscCode: row.ifscCode || row.bankingDetails?.ifsc_code || "",
            panNumber: row.panNumber || row.statutoryDetails?.pan_number || "",
            aadhaarNumber: row.aadhaarNumber || row.statutoryDetails?.aadhaar_number || "",
            uanNumber: row.uanNumber || row.statutoryDetails?.uan_number || "",
            pfNumber: row.pfNumber || row.statutoryDetails?.pf_number || "",
            esiNumber: row.esiNumber || row.statutoryDetails?.esic_number || "",
            grossSalary: row.grossSalary || 0,
            status: row.status || "Active"
        });
        setEditDialogOpen(true);
    };

    const handleBankSelectChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            bankName: value,
            otherBankName: value !== 'Other' ? '' : prev.otherBankName
        }));
    };

    const handleSaveEdit = async () => {
        if (!editingRow) return;

        const resolvedBankName = formData.bankName === 'Other' ? formData.otherBankName.trim() : formData.bankName;
        const userId = editingRow.userId || editingRow.user_id || editingRow.id || editingRow.empId;

        setSaveLoading(true);
        const payload = {
            user_id: userId,
            bankingDetails: {
                account_holder_name: editingRow.accountHolderName || editingRow.employeeName || "",
                account_number: (formData.accountNumber || "").trim(),
                bank_name: resolvedBankName,
                branch_name: (formData.branchName || "").trim(),
                ifsc_code: (formData.ifscCode || "").trim(),
                account_type: editingRow.accountType || "Salary"
            },
            statutoryDetails: {
                pan_number: (formData.panNumber || "").trim(),
                aadhaar_number: (formData.aadhaarNumber || "").trim(),
                uan_number: (formData.uanNumber || "").trim(),
                esic_number: (formData.esiNumber || "").trim() || null,
                pf_number: (formData.pfNumber || "").trim() || null
            }
        };

        try {
            const res = await putData(API_ENDPOINTS.updateEmployeeFinanceAndStatutory, payload);
            if (res?.status === 'SUCCESS' || res?.statusCode === 200) {
                setSnackbar({ open: true, message: `Banking & Statutory details updated successfully`, severity: "success" });
            } else {
                setSnackbar({ open: true, message: res?.message || `Banking & Statutory details saved successfully`, severity: "success" });
            }
            setEditDialogOpen(false);
            setEditingRow(null);
            fetchBankingDetails();
        } catch (error) {
            setSnackbar({ open: true, message: `Banking & Statutory details failed to save`, severity: "error" });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleReset = () => {
        setSearchQuery("");
        setBankStatusFilter("All");
        setStatutoryStatusFilter("All");
        setPage(0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h6" fontWeight="bold">Banking & Statutory Verification</Typography>
                <Typography variant="body2" color="textSecondary">
                    Verify employee bank details, PF, ESI, and gross salary for {payPeriod}
                </Typography>
            </Box>

            {/* Summary Metrics Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Scope Employees"
                        value={summaryMetrics.totalEmployees}
                        icon={<PeopleIcon />}
                        color="#6366F1"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Bank Details Configured"
                        value={summaryMetrics.bankVerifiedCount}
                        icon={<BankIcon />}
                        color="#10B981"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="PF Registered"
                        value={summaryMetrics.pfRegisteredCount}
                        icon={<StatutoryIcon />}
                        color="#F59E0B"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="ESI Registered"
                        value={summaryMetrics.esiRegisteredCount}
                        icon={<VerifiedIcon />}
                        color="#3B82F6"
                    />
                </Grid>
            </Grid>

            {/* Filter Controls */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, md: 5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search by ID, name, bank, or PF number"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    />
                </Grid>
                <Grid item size={{ xs: 6, md: 2.5 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Bank Status</InputLabel>
                        <Select
                            label="Bank Status"
                            value={bankStatusFilter}
                            onChange={(e) => { setBankStatusFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="All">All Bank Statuses</MenuItem>
                            <MenuItem value="Complete">Complete</MenuItem>
                            <MenuItem value="Incomplete">Incomplete</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item size={{ xs: 6, md: 2.5 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Statutory Status</InputLabel>
                        <Select
                            label="Statutory Status"
                            value={statutoryStatusFilter}
                            onChange={(e) => { setStatutoryStatusFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="All">All Statutory Statuses</MenuItem>
                            <MenuItem value="Complete">Complete</MenuItem>
                            <MenuItem value="Incomplete">Incomplete</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, md: 2 }}>
                    <Button fullWidth startIcon={<ResetIcon />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            {/* Data Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 950 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee Name</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Gross Salary</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Bank Name</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Account Number</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>IFSC Code</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>PF Number</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>ESI Number</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {filteredList.length > 0 ? (
                            filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.id || row.empId} hover sx={{ '&:hover': { bgcolor: '#F1F5F9 !important' } }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{row.empId}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.employeeName}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                        ₹{row.grossSalary?.toLocaleString()}
                                    </TableCell>
                                    <TableCell>{row.bankName}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.accountNumber}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.ifscCode}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{row.pfNumber}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{row.esiNumber}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit Banking & Statutory Details">
                                            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                    <NoDataIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 1 }} />
                                    <Typography variant="h6" color="textSecondary">No Employee Banking Details Found</Typography>
                                    <Typography variant="body2" color="textSecondary">Try adjusting search or filter options</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />

            {/* Edit / Verify Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
                    <Typography variant="h6" fontWeight="bold">Banking & Statutory Details</Typography>
                    <IconButton onClick={() => setEditDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {editingRow && (
                        <Stack spacing={2.5} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {editingRow.employeeName} ({editingRow.empId})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Update bank details, statutory numbers, and gross salary
                                </Typography>
                            </Box>

                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                Banking Information
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Bank Name</InputLabel>
                                        <Select
                                            label="Bank Name"
                                            value={formData.bankName}
                                            onChange={handleBankSelectChange}
                                        >
                                            <MenuItem value=""><em>Select Bank</em></MenuItem>
                                            {mockBanks.map((bank) => (
                                                <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {formData.bankName === 'Other' && (
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            label="Other Bank Name"
                                            size="small"
                                            fullWidth
                                            value={formData.otherBankName}
                                            onChange={(e) => setFormData({ ...formData, otherBankName: e.target.value })}
                                        />
                                    </Grid>
                                )}
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Branch Name"
                                        size="small"
                                        fullWidth
                                        value={formData.branchName}
                                        onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Account Number"
                                        size="small"
                                        fullWidth
                                        value={formData.accountNumber}
                                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="IFSC Code"
                                        size="small"
                                        fullWidth
                                        value={formData.ifscCode}
                                        onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Gross Salary (₹)"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formData.grossSalary}
                                        onChange={(e) => setFormData({ ...formData, grossSalary: e.target.value })}
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                Statutory Information
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="PAN Number"
                                        size="small"
                                        fullWidth
                                        inputProps={{ maxLength: 10 }}
                                        value={formData.panNumber}
                                        onChange={(e) => setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Aadhaar Number"
                                        size="small"
                                        fullWidth
                                        inputProps={{ maxLength: 12 }}
                                        value={formData.aadhaarNumber}
                                        onChange={(e) => setFormData({ ...formData, aadhaarNumber: e.target.value.replace(/\D/g, '') })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="UAN Number"
                                        size="small"
                                        fullWidth
                                        value={formData.uanNumber}
                                        onChange={(e) => setFormData({ ...formData, uanNumber: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="PF Number"
                                        size="small"
                                        fullWidth
                                        value={formData.pfNumber}
                                        onChange={(e) => setFormData({ ...formData, pfNumber: e.target.value })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="ESI Number"
                                        size="small"
                                        fullWidth
                                        value={formData.esiNumber}
                                        onChange={(e) => setFormData({ ...formData, esiNumber: e.target.value })}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            label="Status"
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                            <MenuItem value="Pending">Pending</MenuItem>
                                            {formData.status && !["Active", "Inactive", "Pending"].includes(formData.status) && (
                                                <MenuItem value={formData.status}>{formData.status}</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    <Button variant="outlined" startIcon={<RejectIcon />} onClick={() => setEditDialogOpen(false)} disabled={saveLoading}>
                        Cancel
                    </Button>
                    <Button variant="contained" startIcon={<ApproveIcon />} onClick={handleSaveEdit} disabled={saveLoading}>
                        {saveLoading ? <CircularProgress size={20} /> : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default BankingAndStatutoryStepper;
